import { useContext, useEffect, useRef } from "react";
import axios from "axios";
import { useQueryClient } from "react-query";
import { VendorContext } from "contexts/VendorContext";
import { useHeritageConnection } from "../VendorBody/VendorItems/VendorConnectionModal/hooks/useHeritageConnection";
import { isHeritageCompanyConnected } from "../utils/heritageConnectionStatus";
import { heritageApiErrorMessage } from "../components/heritageApiErrorMessage";
import type { Vendor } from "types/vendor";

/** Attempt company Heritage connection when viewing a Heritage-integrated vendor. */
export function useHeritageAutoConnect(vendor: Vendor | null | undefined) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useHeritageConnection();
  const {
    reportHeritageConnectStart,
    reportHeritageConnectSuccess,
    reportHeritageConnectFailure,
  } = useContext(VendorContext);
  const attemptedForVendorRef = useRef<number | null>(null);

  useEffect(() => {
    if (!vendor?.is_heritage || vendor.vendor_id == null) return;
    if (isLoading) return;
    if (attemptedForVendorRef.current === vendor.vendor_id) return;

    if (isHeritageCompanyConnected(data)) return;

    attemptedForVendorRef.current = vendor.vendor_id;
    reportHeritageConnectStart();

    axios
      .post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/connect`)
      .then(() => {
        reportHeritageConnectSuccess();
        queryClient.invalidateQueries("heritage-connection");
        queryClient.invalidateQueries(["heritage-auth-status"]);
      })
      .catch((err) => {
        const message =
          heritageApiErrorMessage(err) ??
          "Unable to connect to Heritage. Check company credentials and try again.";
        reportHeritageConnectFailure(message);
        console.error("[Heritage] Auto-connect failed:", err);
        attemptedForVendorRef.current = null;
      });
  }, [
    vendor?.vendor_id,
    vendor?.is_heritage,
    isLoading,
    data,
    queryClient,
    reportHeritageConnectStart,
    reportHeritageConnectSuccess,
    reportHeritageConnectFailure,
  ]);
}
